/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useState, useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import { Calendar } from './Calendar'
import { Box } from '@mui/material'
import { EmtButton, EmtIcon, useMobile } from 'emt-components-v2'
import { endOfDay, startOfDay } from 'date-fns'

interface DateRange {
  start: Date | null
  end: Date | null
}

interface Range {
  start: Date
  end: Date
  title?: string
  time?: string
  activity?: string
}

interface CalendarDay {
  '@type': string
  '@id': string
  type: string
  isEnd: boolean
  isStart: boolean
  dateTime: string
}

interface LoadingState {
  add: boolean
  remove: boolean
  fetch: boolean
  delete: Set<string>
}

interface Props {
  days: CalendarDay[]
  onAddRange?: (newRange: Range, slug: string) => void
  loading?: LoadingState
  currentMonth: Date
  setCurrentMonth: (date: Date) => void
  viewOnly?: boolean
}

export const DisponibilityCalendar: React.FC<Props> = ({
  days,
  onAddRange,
  loading,
  currentMonth,
  setCurrentMonth,
  viewOnly = false,
}) => {
  const { t } = useTranslation('petsitter')
  const [isMobile] = useMobile()
  const [selectedRange, setSelectedRange] = useState<DateRange>({ start: null, end: null })
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null)

  const handleDateClick = (day: number, monthOffset = 0): void => {
    if (viewOnly) return

    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset)

    const clickedDate = new Date(newMonth.getFullYear(), newMonth.getMonth(), day)

    if (monthOffset !== 0) {
      setCurrentMonth(newMonth)
    }

    // Helper pour normaliser les heures
    const startOfDay = (date: Date) => {
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      return d
    }

    const endOfDay = (date: Date) => {
      const d = new Date(date)
      d.setHours(23, 59, 59, 999)
      return d
    }

    if (!selectedRange.start || selectedRange.end) {
      // START → 00:00:00.000
      setSelectedRange({
        start: startOfDay(clickedDate),
        end: null,
      })
      setHoveredDate(null)
    } else {
      let start = selectedRange.start
      let end = clickedDate

      if (clickedDate < selectedRange.start) {
        start = clickedDate
        end = selectedRange.start
      }

      setSelectedRange({
        start: startOfDay(start),
        end: endOfDay(end), // END → 23:59:59.999
      })

      setHoveredDate(null)
    }
  }

  const handleAddRangeClick = (slug: string): void => {
    if (selectedRange.start) {
      const rawStart = selectedRange.start
      const rawEnd = selectedRange.end ?? selectedRange.start

      const newRange: Range = {
        start: startOfDay(rawStart),
        end: endOfDay(rawEnd),
      }

      onAddRange?.(newRange, slug)

      setSelectedRange({ start: null, end: null })
      setHoveredDate(null)
    }
  }

  const handleDateHover = (date: Date | null) => {
    if (selectedRange.start && !selectedRange.end) {
      setHoveredDate(date)
    } else {
      setHoveredDate(null)
    }
  }

  // Utility function to check if a date is in the current month
  const isDateInCurrentMonth = (date: Date, currentMonth: Date): boolean => {
    return date.getFullYear() === currentMonth.getFullYear() && date.getMonth() === currentMonth.getMonth()
  }

  // Utility function to check if a date is the first day of its month
  const isFirstMonthDate = (date: Date): boolean => {
    return date.getDate() === 1
  }

  // Utility function to check if a date is the last day of its month
  const isLastMonthDate = (date: Date): boolean => {
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
    return date.getDate() === lastDay
  }

  // Reusable function to filter and map days based on type
  const useFilteredDays = (days: CalendarDay[], currentMonth: Date, type: string) => {
    return useMemo(() => {
      return days
        .filter((day) => day.type === type && isDateInCurrentMonth(new Date(day.dateTime), currentMonth))
        .map((day) => {
          const date = new Date(day.dateTime)
          return {
            date,
            type: day.type,
            isStart: day.isStart || isFirstMonthDate(date),
            isEnd: day.isEnd || isLastMonthDate(date),
          }
        })
    }, [days, currentMonth, type])
  }

  const filteredMainEventDays = useFilteredDays(days, currentMonth, 'ALL_SERVICES_MAIN_EVENT_AVAILABILITY')

  const filteredAvailableDays = useFilteredDays(days, currentMonth, 'AVAILABILITY')

  const filteredUnavailableDays = useFilteredDays(days, currentMonth, 'UNAVAILABILITY')

  return (
    <>
      <Calendar
        weeksPerRow={viewOnly && !isMobile ? 2 : 1}
        viewOnly={viewOnly}
        currentMonth={currentMonth}
        selectedRange={selectedRange}
        hoveredDate={hoveredDate}
        mainEventDays={filteredMainEventDays}
        availableDays={filteredAvailableDays}
        unavailableDays={filteredUnavailableDays}
        setCurrentMonth={setCurrentMonth}
        handleDateClick={handleDateClick}
        handleDateHover={handleDateHover}
      />
      {onAddRange && (
        <Box sx={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <EmtButton
            onClick={() => handleAddRangeClick('UNAVAILABILITY')}
            color="purple"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ '& .emt-icons': { fontSize: '15px!important' } }}
            disabled={!selectedRange.start}
            loader={loading && loading.remove}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '8px',
              }}
            >
              <EmtIcon size="small" className="emt-minus-circle" />
            </Box>
            {t('calendar.unavailable')}
          </EmtButton>
          {/* <EmtButton
            onClick={() => handleAddRangeClick('ALL_SERVICES_AVAILABILITY')}
            color="purple"
            size="small"
            fullWidth
            disabled={!selectedRange.start}
            loader={loading && loading.add}
          >
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: '50%',
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '8px',
              }}
            >
              <EmtIcon
                className="emt-add-small"
                sx={{
                  color: selectedRange.start ? '#AE59C6' : '#F4D4FD',
                }}
              />
            </Box>
            {t('calendar.available')}
          </EmtButton> */}
        </Box>
      )}
    </>
  )
}
